import React, { useState } from 'react';
import { db, auth } from './firebase';
import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

const S = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#0A0E1Aee', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#111827', border: '1px solid #1E3A5F', borderRadius: 12, padding: 24, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
  title: { fontSize: 18, fontWeight: 900, color: '#F8FAFC', marginBottom: 4 },
  sub: { fontSize: 12, color: '#9CA3AF', marginBottom: 20 },
  label: { fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6, display: 'block', marginTop: 14 },
  input: { background: '#1E3A5F22', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  select: { background: '#111827', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  btn: (color) => ({ background: color || '#1E3A5F', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }),
  error: { color: '#D32F2F', fontSize: 12, marginTop: 8 },
  codeBox: { background: '#0A0E1A', border: '1px solid #C9A84C', borderRadius: 8, padding: '14px 16px', fontFamily: 'monospace', fontSize: 15, color: '#C9A84C', wordBreak: 'break-all', marginTop: 12 },
  warn: { fontSize: 12, color: '#F57F17', marginTop: 10 },
};

const INVITE_ROLES = ['officer', 'supervisor', 'head_of_security', 'facility_manager', 'general_manager', 'admin'];
const EXPIRY_DAYS = 7;

export default function InviteOfficerModal({ tenantId, onClose }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('officer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteId, setInviteId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const admin = auth.currentUser;
      // setDate, not +N*24h: DST days are 23 or 25 hours, so millisecond
      // arithmetic drifts an hour across a transition. Same defect class as
      // the ShiftScheduler toISOString bug.
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS);
      const expiresAt = Timestamp.fromDate(expiryDate);
      const ref = await addDoc(collection(db, 'invites'), {
        tenant_id: tenantId,
        role,
        email: email.trim() ? email.trim().toLowerCase() : null,
        created_by: admin?.uid || null,
        created_by_email: admin?.email || null,
        status: 'pending',
        expires_at: expiresAt,
        created_at: serverTimestamp(),
      });
      setInviteId(ref.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (inviteId) navigator.clipboard.writeText(inviteId);
  };

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={S.title}>Invite officer</div>
            <div style={S.sub}>Generates a one-time invite code the officer enters at signup in the app</div>
          </div>
          <button onClick={onClose} style={{ ...S.btn('#D32F2F22'), color: '#D32F2F', padding: '6px 12px' }}>✕</button>
        </div>

        {!inviteId ? (
          <form onSubmit={handleSubmit}>
            <label style={S.label}>Email (optional — locks the invite to this address)</label>
            <input style={S.input} type="email" placeholder="officer@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />

            <label style={S.label}>Role</label>
            <select style={S.select} value={role} onChange={(e) => setRole(e.target.value)}>
              {INVITE_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
            </select>

            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 14 }}>Expires in {EXPIRY_DAYS} days</div>

            {error && <div style={S.error}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button type="button" onClick={onClose} style={S.btn('#1E3A5F22')}>Cancel</button>
              <button type="submit" disabled={loading} style={S.btn('#C9A84C')}>{loading ? 'Creating...' : 'Create invite'}</button>
            </div>
          </form>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: '#F8FAFC' }}>Invite created. Share this code with the officer — it will not be shown again.</div>
            <div style={S.codeBox}>{inviteId}</div>
            <div style={S.warn}>This invite is not retrievable after you close this dialog — copy it now.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={copyCode} style={S.btn('#1E3A5F')}>Copy code</button>
              <button onClick={onClose} style={S.btn('#C9A84C')}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
